import React from 'react';
import BottomScrollListener from '@dashboard/components/BottomScrollListener';
import { useInfiniteQuery } from '@tanstack/react-query';
import { stringify } from 'query-string';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import usePage from '@dashboard/hooks/usePage';
import Spinner from '@dashboard/components/Spinner';
import CardListItem from '@dashboard/components/CardListItem';
import EmptyState from '@dashboard/components/EmptyState';
import TableRowElement from '@dashboard/components/TableRowElement';
import Badge from '@dashboard/components/Badge';
import { fetchInfinite } from '@/dashboard/services/request';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import getPriceLabel from '@/dashboard/services/translation/getPriceLabel';
import { formatDateTime } from '@/dashboard/lib/format-date';

export default function GuestsList({ sort, filter = {}, search }) {
  const { page } = usePage();
  const { sellerId, token } = useAuth();

  const { data, fetchNextPage, isFetchingNextPage, isError, isLoading } = useInfiniteQuery({
    queryKey: ['pageCustomers', sellerId, sort, search, filter, page._id],
    queryFn: async ({ pageParam }) => {
      return fetchInfinite(
        `/sellers/${sellerId}/pages/${page._id}/guests?${stringify({
          filter: JSON.stringify(filter),
          search,
          limit: 10,
          page: pageParam,
          sort: JSON.stringify(sort),
        })}`,
        token,
      );
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.data.has_more) return undefined;

      return allPages.length;
    },
  });

  const hasFetchedAllPages = data?.pages?.[data?.pages?.length - 1]?.data.has_more === false;

  const title = 'guests';

  if (isLoading) {
    return <Spinner className="m-4" />;
  }

  if (isError) {
    return <div className="m-4">Unable to show {title}. Please refresh or contact support.</div>;
  }

  const isEmpty = !data?.pages?.[0]?.data?.guestList.length;

  if (isEmpty) {
    return (
      <EmptyState>
        <EmptyState.Title>No {title} yet</EmptyState.Title>
        <EmptyState.Subtitle>Your {title} will show up here.</EmptyState.Subtitle>
      </EmptyState>
    );
  }
  return (
    <div>
      <BottomScrollListener onBottom={fetchNextPage}>
        {data?.pages?.map(data => {
          return data?.data.guestList.map(guest => {
            return <GuestListItem key={guest._id} guest={guest} />;
          });
        })}
      </BottomScrollListener>

      {isFetchingNextPage && <Spinner className="m-4" />}

      {hasFetchedAllPages && (
        <div className="text-sm text-center text-gray-500 mt-2">No more guests to show</div>
      )}
    </div>
  );
}

const GuestListItem = ({ guest }) => {
  const { page } = usePage();

  const getStatusBadgeAppearance = status => {
    if (status === 'PAID') return 'success';
    if (status === 'CANCELED') return 'error';
    return 'info';
  };

  const formatStatus = status => {
    if (status === 'PAID') return 'Valid';
    if (status === 'CANCELED') return 'Canceled';
    return status;
  };

  return (
    <CardListItem href={`/customers/${guest.customer}`} key={guest._id}>
      <TableRowElement size="fill" align="right">
        <div>
          <div className="font-medium">{guest.customerEmail || ''}</div>
          <div className="text-sm text-gray-500">{guest.customerName || ''}</div>
        </div>
      </TableRowElement>

      <TableRowElement size="xl">
        <div className="overflow-hidden text-ellipsis whitespace-nowrap w-full">
          {guest.ticketType?.name || ''}
        </div>
      </TableRowElement>

      <TableRowElement size="medium">{guest.orderId}</TableRowElement>

      <TableRowElement size="large">
        <div className="font-medium">{guest.ticketShortId}</div>
      </TableRowElement>

      <TableRowElement size="medium">
        <div className="font-medium">
          {guest.originalPrice === 0 || !guest.originalPrice
            ? 'Free'
            : getPriceLabel({
                amount: guest.originalPrice,
                currency: guest.currency || page.currency,
              })}
        </div>
      </TableRowElement>

      <TableRowElement size="xl">
        <div>{formatDateTime(guest.orderedAt)}</div>
      </TableRowElement>

      <TableRowElement size="medium">
        <Badge appearance={getStatusBadgeAppearance(guest.status)}>
          {formatStatus(guest.status)}
        </Badge>
      </TableRowElement>

      <TableRowElement size="large">
        {guest.status === 'CANCELED' ? (
          <Badge appearance="warning">Void</Badge>
        ) : (
          <>
            {guest.latestCheckIn != null && guest.latestCheckIn.status === 'CHECKEDIN' ? (
              <Badge appearance="success">
                <CheckCircleIcon className="h-3 w-3 mr-1" aria-hidden="true" />
                Checked in
              </Badge>
            ) : (
              <Badge appearance="info">Pending</Badge>
            )}
          </>
        )}
      </TableRowElement>
    </CardListItem>
  );
};
